import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  IApiRequest,
  IApiToastMessageConfig,
  ICreateCityRequest,
  ICityResponse,
} from "@miplatform/types";

export const useCities = () => {
  const [cities, setCities] = useState<ICityResponse[]>([]);
  const [recentCities, setRecentCities] = useState<ICityResponse[]>([]);

  const stateManager = useComponentState();

  const getCities = async ({
    top,
    skip,
    filters,
    search = "",
  }: IApiRequest = {}) => {
    const searchFilter = search ? { cityName: { contains: search } } : {};

    const combinedFilters = {
      ...filters,
      ...searchFilter,
    };

    return stateManager.run(async () => {
      const response = await homeService.cities.getAll({
        top,
        skip,
        sort: "cityName",
        filters: combinedFilters,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setCities(response.data);
      }
      return response;
    });
  };

  const getRecentCities = ({
    sort,
    top,
    filters,
    onSuccess,
  }: IApiRequest) => {
    return stateManager.run(async () => {
      const response = await homeService.cities.getAll({
        sort,
        top,
        filters,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setRecentCities(response.data);
        onSuccess?.(response.data);
      }
      return response;
    });
  };

  const getCityById = async (id: string) => {
    return stateManager.run(async () => {
      return homeService.cities.getById({ id });
    });
  };

  const createCity = async ({ data }: { data: ICreateCityRequest }) => {
    const toastMessageConfig: IApiToastMessageConfig = {
      success: { text: `${data.cityName} created successfully.` },
      failure: { text: `Failed to create ${data.cityName}. Please try again.` },
    };
    return stateManager.run(async () => {
      return homeService.cities.create({ data, toastMessageConfig });
    });
  };

  const updateCity = async ({
    id,
    data,
    inspectionAuthorityIds,
  }: {
    id: string;
    data: ICityResponse;
    inspectionAuthorityIds?: string[];
  }) => {
    const toastMessageConfig: IApiToastMessageConfig = {
      success: { text: `${data.cityName} updated successfully.` },
      failure: { text: `Failed to update ${data.cityName}. Please try again.` },
    };
    return stateManager.run(async () => {
      return homeService.cities.update({
        id,
        data: { ...data, ...(inspectionAuthorityIds !== undefined && { inspectionAuthorityIds }) },
        toastMessageConfig,
      });
    });
  };

  const deleteCity = async ({
    id,
    modifiedOn,
    name,
  }: {
    id: string;
    modifiedOn: string | undefined;
    name?: string;
  }) => {
    const toastMessageConfig: IApiToastMessageConfig = {
      success: { text: `${name ?? "City"} deleted successfully.` },
      failure: { text: `Failed to delete ${name ?? "City"}. Please try again.` },
    };
    return stateManager.run(async () => {
      return homeService.cities.delete({ id, modifiedOn, toastMessageConfig });
    });
  };

  return {
    cities,
    setCities,
    recentCities,
    setRecentCities,
    getCities,
    getRecentCities,
    getCityById,
    createCity,
    updateCity,
    deleteCity,
    isLoading: stateManager.isBusy(),
  };
};